import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, PieChart, Globe, Zap, ArrowRight, CheckCircle2, Users } from 'lucide-react';
import { useT } from '../components/site/i18n';
import { Link } from 'react-router-dom';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-650" data-mp="1ar31vwfsmi2i">
    {children}
  </motion.div>;
export function ParaInvestidor() {
  const t = useT();
  return <div className="min-h-screen bg-brand-cream dark:bg-gray-900 pt-24 pb-24 transition-colors duration-300" data-id="element-651" data-mp="r3n6pvnepn7y">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-24" data-id="element-652" data-mp="w0vo3u0qdn85">
        <FadeIn className="text-center max-w-4xl mx-auto" data-id="element-653" data-mp="36vmkvl033jdv">
          <span className="text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-6 flex items-center justify-center gap-2" data-id="element-654" data-mp="36x7ffamef2bq">
            <TrendingUp size={16} className="text-brand-yellow" data-id="element-655" data-mp="2y1i0nkfgh67b" />
            {t.paraInvestidor.heroEyebrow}
          </span>
          <h1 className="text-5xl md:text-7xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-656" data-mp="2t3g90b3yryk8">
            {t.paraInvestidor.heroTitle1}{' '}
            <span className="relative inline-block" data-id="element-657" data-mp="2j4r0va1vdsh6">
              <span className="relative z-10 font-display text-brand-green dark:text-brand-green-light font-normal text-6xl md:text-8xl tracking-normal transform -rotate-2 inline-block" data-id="element-658" data-mp="2f3niogwcnkcs">
                {t.paraInvestidor.heroTitle2}
              </span>
              <svg className="absolute -bottom-2 left-0 w-full h-4 text-brand-yellow -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-659" data-mp="dc24e9xgwpaa">
                <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" data-id="element-660" data-mp="rgsx8zx5px2g" />
              </svg>
            </span>
          </h1>
          <p className="text-xl text-brand-charcoal/70 dark:text-gray-300 leading-relaxed mb-10" data-id="element-661" data-mp="nhadw8kzhu1q">
            {t.paraInvestidor.heroDesc}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-662" data-mp="1f7okrkuky63w">
            <Link to="/contato" className="bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-full font-bold transition-all shadow-sm hover:shadow-md flex items-center gap-2 w-full sm:w-auto justify-center" data-id="element-663" data-mp="1f5c7vm3oilxp">
              {t.paraInvestidor.ctaButton} <ArrowRight size={18} data-id="element-664" data-mp="3m0hbw5agoqjx" />
            </Link>
          </div>
        </FadeIn>
      </section>

      {/* Market Section */}
      <section className="bg-white dark:bg-gray-800 py-24 transition-colors duration-300" data-id="element-665" data-mp="3jd6u08ikypzi">
        <div className="max-w-7xl mx-auto px-6" data-id="element-666" data-mp="3eevizugju9fp">
          <FadeIn className="text-center max-w-3xl mx-auto mb-16" data-id="element-667" data-mp="3ee3suz5q13wt">
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-668" data-mp="3semrxgs7kzmu">
              {t.paraInvestidor.marketTitle}
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-669" data-mp="2zp7kt23nzqw7">
            <FadeIn delay={0.1} className="bg-gray-50 dark:bg-gray-900 p-10 rounded-3xl border border-gray-100 dark:border-gray-700" data-id="element-670" data-mp="2znmpvbdfu5xt">
              <div className="w-12 h-12 bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light rounded-2xl flex items-center justify-center mb-6" data-id="element-671" data-mp="39gahzsth3lq7">
                <PieChart size={24} data-id="element-672" data-mp="2gyptn1yhd3cy" />
              </div>
              <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-673" data-mp="2gxenwwlh5lj7">
                {t.paraInvestidor.marketB2cTitle}
              </h3>
              <ul className="space-y-4" data-id="element-674" data-mp="2pnwserfvqjsc">
                {t.paraInvestidor.marketB2cList.map((item, i) => <li key={i} className="flex items-center gap-3 text-brand-charcoal/80 dark:text-gray-300" data-id="element-675" data-mp="2umq6om2zqetk" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-brand-green" data-id="element-676" data-mp="34c9rvye6ekrl" />
                    <span data-id="element-677" data-mp="34dkrdm0l96cw">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-gray-50 dark:bg-gray-900 p-10 rounded-3xl border border-gray-100 dark:border-gray-700" data-id="element-678" data-mp="3dc6abi9z2ne7">
              <div className="w-12 h-12 bg-brand-yellow/20 text-brand-yellow-dark dark:text-brand-yellow rounded-2xl flex items-center justify-center mb-6" data-id="element-679" data-mp="3i9omdhjadiqf">
                <Globe size={24} data-id="element-680" data-mp="1nytbu9ojjtl0" />
              </div>
              <h3 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-681" data-mp="1swl0mutw2zkz">
                {t.paraInvestidor.marketB2gTitle}
              </h3>
              <ul className="space-y-4" data-id="element-682" data-mp="a31ix647pcyc">
                {t.paraInvestidor.marketB2gList.map((item, i) => <li key={i} className="flex items-center gap-3 text-brand-charcoal/80 dark:text-gray-300" data-id="element-683" data-mp="58ngwkv1o7kp" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-brand-yellow-dark dark:text-brand-yellow" data-id="element-684" data-mp="3sgr5vzgjcv76" />
                    <span data-id="element-685" data-mp="oy7ndonnt3pk">{item}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Thesis Section */}
      <section className="py-24" data-id="element-686" data-mp="f0tj0nf0g5dm">
        <div className="max-w-7xl mx-auto px-6" data-id="element-687" data-mp="ezi69y3l7nad">
          <div className="grid lg:grid-cols-3 gap-12" data-id="element-688" data-mp="f2xo38onbafh">
            <FadeIn className="lg:col-span-1" data-id="element-689" data-mp="34qofrow6lzb7">
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-6" data-id="element-690" data-mp="2zqsdpv71ohya">
                {t.paraInvestidor.thesisTitle}
              </h2>
              <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-8" data-id="element-691" data-mp="318r30l4yvm3u">
                {t.paraInvestidor.whyNowDesc}
              </p>
            </FadeIn>

            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-8" data-id="element-692" data-mp="2s6hqjpq51int">
              {[{
              title: t.paraInvestidor.thesis1Title,
              desc: t.paraInvestidor.thesis1Desc,
              icon: Users
            }, {
              title: t.paraInvestidor.thesis2Title,
              desc: t.paraInvestidor.thesis2Desc,
              icon: TrendingUp
            }, {
              title: t.paraInvestidor.thesis3Title,
              desc: t.paraInvestidor.thesis3Desc,
              icon: Zap
            }].map((thesis, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-693" data-mp="24hfagmb5jxvf" data-mpi={i}>
                  <div className="w-12 h-12 bg-brand-green/10 text-brand-green-dark dark:text-brand-green-light rounded-2xl flex items-center justify-center mb-6" data-id="element-694" data-mp="1fxvfkafxc0ez">
                    <thesis.icon size={24} data-id="element-695" data-mp="1pv9kbcshj0rg" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-696" data-mp="1px2tayqklx2m">
                    {thesis.title}
                  </h3>
                  <p className="text-brand-charcoal/70 dark:text-gray-400 leading-relaxed" data-id="element-697" data-mp="1ywg53y262xqi">
                    {thesis.desc}
                  </p>
                </FadeIn>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-5xl mx-auto px-6 pb-12" data-id="element-698" data-mp="2ml8utcs749co">
        <FadeIn className="bg-brand-charcoal dark:bg-gray-800 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden" data-id="element-699" data-mp="2mlseaxxhv5st">
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-700" data-mp="2ihue0jwfnoy6"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-brand-yellow/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-701" data-mp="3twwsqu7ldhgv"></div>

          <div className="relative z-10" data-id="element-702" data-mp="2pakspduasu7">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-703" data-mp="6tibgs3uyiv2">
              {t.paraInvestidor.ctaTitle}
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-704" data-mp="369ongca5y8eu">
              {t.paraInvestidor.ctaDesc}
            </p>
            <Link to="/contato" className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-full font-bold transition-all shadow-sm hover:shadow-md" data-id="element-705" data-mp="3f8tobp976du6">
              {t.paraInvestidor.ctaButton} <ArrowRight size={18} data-id="element-706" data-mp="2nhdtj7xhlksv" />
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>;
}